import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../lib/api-client';
import { useToastStore } from '../../../stores/toast-store';

export type OperationMode = 'H_a' | 'H_b' | 'H_c' | 'H_d';

/**
 * Task 59 — useSetOperationMode canonical.
 *
 * Mutation hook para alternar modo operacional (H_a / H_b / H_c / H_d) da
 * conversa. On success invalida cache de detail para refletir novo modo.
 *
 * Capability gate backend: `conversation-lifecycle-strict` (v1) — Wave 2
 * refina por modo (`ai-conductor-h-b` Team+ / `ai-conductor-h-c` Pro+).
 */
export function useSetOperationMode(conversationId: string | undefined): {
  mutate: (mode: OperationMode) => Promise<void>;
  isPending: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (mode: OperationMode) => {
      if (conversationId === undefined) {
        throw new Error('conversationId obrigatório para alternar modo operacional');
      }
      return api.setOperationMode(conversationId, mode);
    },
    onSuccess: (data) => {
      if (conversationId === undefined) return;
      void queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      useToastStore.getState().push({
        variant: 'success',
        title: `Modo alterado para ${data.mode}`,
        description: `Anterior: ${data.previousMode}`,
        durationMs: 3500,
      });
    },
    onError: (err) => {
      useToastStore.getState().push({
        variant: 'error',
        title: 'Falha ao alternar modo operacional',
        description: err instanceof Error ? err.message : 'Erro desconhecido',
        durationMs: 6000,
      });
    },
  });

  return {
    mutate: async (mode) => {
      await mutation.mutateAsync(mode);
    },
    isPending: mutation.isPending,
    error: mutation.error,
  };
}
